import React, { useState, useEffect } from 'react';
import {
  Clock, CheckCircle, XCircle, AlertCircle, RefreshCw, Loader2,
  MessageSquare, Building, User, Calendar, Check, X, Eye,
  ChevronRight, Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const OpsPendingChangesPage = () => {
  const [changes, setChanges] = useState({ pending: [], pending_count: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedChange, setSelectedChange] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [notes, setNotes] = useState('');

  const getOpsToken = () => localStorage.getItem('ops_token');

  useEffect(() => {
    loadChanges();
  }, []);

  const loadChanges = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/pending-changes/ops/all?status=pending`, {
        headers: { 'X-Ops-Token': getOpsToken() }
      });
      if (res.ok) {
        const data = await res.json();
        setChanges(data);
      }
    } catch (err) {
      console.error('Failed to load changes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (changeId) => {
    setActionLoading(changeId);
    try {
      const res = await fetch(`${API_URL}/api/pending-changes/ops/${changeId}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Ops-Token': getOpsToken()
        },
        body: JSON.stringify({ notes: notes || 'Approved by ops team' })
      });
      if (res.ok) {
        setSelectedChange(null);
        setNotes('');
        loadChanges();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to approve');
      }
    } catch (err) {
      alert('Failed to approve change');
    } finally {
      setActionLoading(null);
    }
  };

  const getChangeTypeIcon = (type) => {
    const icons = {
      'greeting_message': MessageSquare,
      'business_hours': Clock,
      'profile': Building
    };
    return icons[type] || AlertCircle;
  };

  const formatValue = (value) => {
    if (!value) return '—';
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'object') {
        return JSON.stringify(parsed, null, 2);
      }
      return value;
    } catch {
      return value;
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pending Changes</h1>
          <p className="text-gray-500">Review customer setting change requests</p>
        </div>
        <button
          onClick={loadChanges}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 shadow-sm"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 mb-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-sm">Pending Review</p>
            <p className="text-2xl font-bold text-gray-900">{changes.pending_count || changes.pending?.length || 0}</p>
          </div>
          <div className="p-3 rounded-lg bg-yellow-100">
            <Clock className="w-6 h-6 text-yellow-600" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Changes List */}
        <div className="col-span-1 bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h2 className="font-semibold text-gray-900">Pending ({changes.pending?.length || 0})</h2>
          </div>
          <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : changes.pending?.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No pending changes</p>
              </div>
            ) : (
              changes.pending?.map(change => {
                const Icon = getChangeTypeIcon(change.change_type);
                return (
                  <div
                    key={change.id}
                    onClick={() => setSelectedChange(change)}
                    className={`p-4 cursor-pointer hover:bg-emerald-50 transition-colors ${
                      selectedChange?.id === change.id ? 'bg-emerald-50 border-l-4 border-l-emerald-600' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="w-4 h-4 text-emerald-600" />
                      <span className="font-medium text-gray-900">{change.client_name}</span>
                    </div>
                    <div className="text-sm text-gray-500 capitalize">
                      {change.change_type?.replace('_', ' ')}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(change.submitted_at).toLocaleDateString()}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Detail View */}
        <div className="col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          {selectedChange ? (
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{selectedChange.client_name}</h2>
                  <p className="text-sm text-gray-500 capitalize">
                    {selectedChange.change_type?.replace('_', ' ')} • {selectedChange.field_name}
                  </p>
                </div>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                  Pending
                </span>
              </div>

              {/* Content */}
              <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                {/* Old Value */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h3 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                    <X className="w-4 h-4" />
                    Current Value
                  </h3>
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap bg-white rounded p-3 border border-red-100 overflow-x-auto">
                    {formatValue(selectedChange.old_value) || 'Not set'}
                  </pre>
                </div>

                {/* New Value */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h3 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    Requested Value
                  </h3>
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap bg-white rounded p-3 border border-green-100 overflow-x-auto">
                    {formatValue(selectedChange.new_value)}
                  </pre>
                </div>

                {/* Action Area */}
                <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                  <h3 className="font-semibold text-gray-900 mb-3">Review Action</h3>
                  <div className="mb-4">
                    <label className="text-sm text-gray-600 block mb-2">
                      Notes (optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add notes about your decision..."
                      className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 resize-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      rows={3}
                    />
                  </div>
                  <button
                    onClick={() => handleApprove(selectedChange.id)}
                    disabled={actionLoading === selectedChange.id}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-medium shadow-sm"
                  >
                    {actionLoading === selectedChange.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                    Approve & Apply Change
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Note: Only admins can reject changes. Contact admin if needed.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400 min-h-[400px]">
              <div className="text-center">
                <Eye className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Select a change to review</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpsPendingChangesPage;
